import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { IFtpDetailFormValues } from '../../../../@types/model/masterData/ftpDetail/ftpDetailFormValues';
import { PillButton, CustomTooltip, ControlledTextField } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';
import FtpDetailModelHelper from '../../../../@types/model/masterData/ftpDetail/ftpDetailModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';

interface IFtpDetailFormProps {
    isLoading : boolean;
    initialValues : IFtpDetailFormValues;
    onSubmit : (values : IFtpDetailFormValues) => void;
    onClose : () => void;
}

const FtpDetailForm = (props : IFtpDetailFormProps) : React.ReactElement => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IFtpDetailFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(FtpDetailModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const renderPasswordIconButton = useMemo(() => {
        return (
            <CustomTooltip
                className={'aic'}
                title={showPassword ? 'Hide Password' : 'Show Password'}
            >
                <IconButton className={''} onClick={toggleShowPassword}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
            </CustomTooltip>
        );
    }, [showPassword]);

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)} className={'p20 fdc'}>
                <ControlledTextField
                    className={'mb15 wfill'}
                    name={'name'}
                    label={'Name'}
                    control={control}
                    defaultValue={props.initialValues.name}
                    size={'small'}
                />
                <ControlledTextField
                    className={'mb15 wfill'}
                    name={'address'}
                    label={'Address'}
                    control={control}
                    defaultValue={props.initialValues.address}
                    size={'small'}
                />
                <ControlledTextField
                    className={'mb15 wfill'}
                    name={'port'}
                    label={'Port'}
                    control={control}
                    type={'number'}
                    defaultValue={props.initialValues.port}
                    size={'small'}
                />
                <ControlledTextField
                    className={'mb15 wfill'}
                    name={'username'}
                    label={'Username'}
                    control={control}
                    defaultValue={props.initialValues.username}
                    size={'small'}
                />
                <ControlledTextField
                    className={'mb15 wfill'}
                    name={'password'}
                    type={showPassword ? 'text' : 'password'}
                    label={'Password'}
                    control={control}
                    defaultValue={props.initialValues.password}
                    InputProps={{ endAdornment: renderPasswordIconButton }}
                    size={'small'}
                />
                <ControlledTextField
                    className={'mb15 wfill'}
                    name={'path'}
                    label={'Path'}
                    control={control}
                    defaultValue={props.initialValues.path}
                    size={'small'}
                />
                <div className={'flx1 fdr jcfs'}>
                    <ControlledLabelledCheckBox
                        label={'Active?'}
                        name={'isActive'}
                        control={control}
                        defaultValue={props.initialValues.isActive}
                        disabled={props.initialValues.isActive}
                    />
                </div>
                <div className={'fdr flx1 aic jcfe'}>
                    <Button className={'cpd mr20'} type={'button'} onClick={props.onClose}>
                        CANCEL
                    </Button>
                    <PillButton
                        className={'pl30 pr30'}
                        text={'SAVE'}
                        size={'small'}
                        type={'submit'}
                        color={'secondary'}
                        disabled={ !isDirty || !isValid || props.isLoading }
                    />
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default FtpDetailForm;
